<script setup lang="ts">
// @ts-ignore
import i18n from '@/localization/i18n';
import useNavigationStore from '@/stores/ui/navigation'
import Link from '@/views/components/Link.vue'
import { Ref, nextTick, onBeforeUnmount, onMounted, onUnmounted, ref, watch } from 'vue'
import { navigate } from 'vike/client/router'
import { usePageContext } from 'vike-vue/usePageContext'
import TopelaDesktopLogo from './TopelaDesktopLogo.vue';


// Local variables
const t = i18n.global.t
// console.log(t("w_fct_header_proAndPrices"))

// @ts-ignore
const VITE_ASSET_URL = import.meta.env.VITE_ASSET_URL
const sections = ['home', 'app-features', 'pro-user-to', 'client-user', 'contact']

// Refs
const activeSection: Ref<string | null> = ref(null)
const observer: any = ref(null)
const pageContext = usePageContext()

const setTimeoutId = ref<any | null>(null)

const options = {
    root: null,
    rootMargin: '0px',
    threshold: 0.5
};

// Stores
const navigationStore = useNavigationStore()
const isHomePage = ref(pageContext.urlPathname === '/')
// Lifecycle hooks
onMounted(() => {
    // console.log("pageContext", pageContext)
    // console.log("pageContext.urlPathname", pageContext.urlPathname)
    // console.log("isHomePage", isHomePage.value)
    window.addEventListener('scroll', () => {
        sections.forEach(section => {
            if (isActive(section)) {
                activeSection.value = section
            }
        })
    })

    // Create a new Intersection Observer
    observer.value = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                activeSection.value = entry.target.id
            }
        });
    }, options
    );

    // Observe each div item
    sections.forEach(itemId => {
        const item = document.getElementById(itemId);
        if (item) {
            observer.value.observe(item);
        }
    });
})

// Unobserve each div item
const unobserveItems = () => {
    sections.forEach(itemId => {
        const item = document.getElementById(itemId);
        if (item) {
            observer.value.unobserve(item);
        }
    });


}

onUnmounted(() => {
    unobserveItems();
    window.removeEventListener('scroll', () => { })
})

// Functions
const scrollToSection = async (id: string) => {
    await nextTick()
    if (pageContext.urlPathname !== '/') {
        navigationStore.setHomeScrollTo(id)
        await navigate('/')
    } else {
        nextTick(() => {
            const section = document.getElementById(id !== "client-user" ? id : id + '-replace')
            if (section) {
                activeSection.value = id
                setTimeoutId.value = setTimeout(() => {
                    section.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                    })
                }, 300)
            }
        })
    }
}

const unwatchHomeScrollTo = watch(() => navigationStore.homeScrollTo, async (newId) => {
    await nextTick()
    let id = newId
    if (id && id === "home") {
        if (pageContext.urlPathname === '/') {
            const section: any = document.getElementById(id)
            if (section) {
                setTimeoutId.value = setTimeout(() => {
                    section.scrollIntoView({
                        behavior: "smooth"
                    })
                    activeSection.value = id
                    navigationStore.setHomeScrollTo(null)
                }, 500);
            }
        }
    }
    let headerId = navigationStore.headerScrollTo;
    if (headerId && headerId === "pro-user-to" || headerId === "home" || headerId === "contact") {
        activeSection.value = headerId
    }
});


const isActive = (section: string) => {
    const element = document.getElementById(section);
    const rect = element?.getBoundingClientRect();

    if (rect) {
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;

        // Check if the section is at least 30% visible in the viewport
        const isVisible = (rect.top <= windowHeight * 0.7) && (rect.bottom >= 0);
        return isVisible;
    } else {
        return false;
    }
};

onBeforeUnmount(() => {
    if (setTimeoutId.value !== null) clearTimeout(setTimeoutId.value);
    setTimeoutId.value = null;
    unwatchHomeScrollTo()
});
</script>

<template>
    <!-- Header Section Start -->
    <div>
        <nav id="header-public"
            class="navbar navbar-expand-md bg-inverse position-fixed top-0 start-0 scrolling-navbar" :class="isHomePage ? 'pt-4' : 'p-0'" :style="isHomePage ? '' :'height:90px'"
            style="background-color: #233648;">
            <div class="container-md d-flex align-items-center justify-content-between">
                <div class="d-flex align-items-center">
                    <Link href="/" class="navbar-brand mx-0 d-none d-lg-block">
                    <TopelaDesktopLogo :classes="['me-1']" />
                    </Link>
                    <Link href="/" class="navbar-brand mx-0 d-block d-lg-none">
                    <img :src="VITE_ASSET_URL + 'assets/logo/logos_Topela_Phone.svg'" width="50px" alt="Logo topela">
                    </Link>
                </div>

                <ul class="navbar-nav d-flex flex-row align-items-center gap-2 m-0">

                    <li class="nav-item me-1 me-md-5">
                        <Link href="/auth/login/"
                            class="btn m-0 py-2 px-2  px-1 m-0 d-flex align-items-center justify-content-center  text-white link-style"
                            type="button">
                        Connexion
                        </Link>
                    </li>
                    <li class="nav-item ">
                        <Link href="/pro/"
                            target="_blank"
                            class="btn btn-outline-light border-1 border-white rounded d-flex align-items-center justify-content-center custom-btn"
                            type="button">
                        <span class="material-symbols-outlined">engineering</span>&nbsp;<span>{{
                            t('w_fct_csMenu_Professional') }}</span>
                        </Link>
                    </li>

                </ul>
            </div>
        </nav>
    </div>
    <!-- Header Section End -->
</template>

<style scoped>
@import url('@/assets/css/bootstrap-extended.css');
@import url('@/assets/css/animate.css');
@import url('@/assets/css/main.css');
@import url('@/assets/css/responsive.css');

.btn-text-visibility {
    font-size: 16px !important;
    font-weight: normal !important;
}

.btn-text-visibility-mobile {
    font-size: 14px !important;
    font-weight: normal !important;
}

.custom-btn {
    color: white !important
}

.custom-btn:hover {
    color: #233648 !important;
}

.link-style {
    border-radius: 0px !important;
}

.link-style:hover {
    color: #F0A43D !important;
    border-bottom: #F0A43D 1px solid !important;

}

.navbar {
    right: 0px;
    z-index: 1030;
}

.ms-2 {
    margin-left: 2rem !important;
}

@media only screen and (min-width: 600px) {
    .navbar {
        /* right: 17px; */
        right: 1vw;
        width: 99vw;
        z-index: 1030;
    }
}
</style>